import React, { useMemo, useEffect, useContext, useCallback } from 'react';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { useParams, useHistory } from 'react-router-dom';
import PageHeader from '../../../components/PageHeader';
import Loading from '../Loading';
import {
  useJobDetailsQuery,
  useGetReferrerLinkMutation,
} from '../../../__generated__/graphql';
import { recruitmentRoutes } from '../../routes';
import ServerError from '../../../../../components/ServerError';
import { useFlairBreadcrumbHook } from '../../../../../hooks/useFlairBreadcrumbHook';
import { FlairBreadcrumbContext } from '../../../../../context/FlairBreadcrumbContext';
import { useToasts } from '../../../../../context/Toast';
import './index.css';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { EmptyStateCardBody } from '../../../components/EmptyStateCard';
import { useObjectLabels } from '../../../hooks/useObjectLabels';

type RouteParams = {
  jobId: string;
};

const JobDetails: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { jobId } = useParams<RouteParams>();
  const history = useHistory();
  const t = useNamespacedTranslation('recruitment.careerOpportunities');
  const tNavigation = useNamespacedTranslation(
    'navigation.menuItems.recruitment',
  );
  const objectLabels = useObjectLabels();

  const { data, loading, error } = useJobDetailsQuery({
    variables: { jobId },
  });
  const [getReferrerLink, { loading: referrerLinkLoading }] =
    useGetReferrerLinkMutation();
  const { addSuccess, addError } = useToasts();
  const { setBreadcrumbItems } = useContext(FlairBreadcrumbContext);

  const job = data?.job;

  const breadcrumbItems = useMemo(
    () => [
      { label: tNavigation('title') },
      {
        label: tNavigation('subMenuItems.careerOpportunities.title'),
        url: recruitmentRoutes.careerOpportunities.route.create({}),
      },
      ...(job?.Name ? [{ label: job.Name }] : []),
    ],
    [tNavigation, job?.Name],
  );

  useFlairBreadcrumbHook(breadcrumbItems, loading);

  useEffect(() => {
    if (!loading && job?.Name) {
      setBreadcrumbItems(breadcrumbItems);
    }
  }, [loading, job?.Name, breadcrumbItems, setBreadcrumbItems]);

  const requiredSkills = useMemo(() => {
    return (
      job?.jobSkills
        ?.map((js) => ({
          id: js.flair__Skill__c,
          name: js.flair__Skill_Name__c || js.skill?.Name,
        }))
        .filter((skill): skill is { id: string; name: string } =>
          Boolean(skill.name),
        ) ?? []
    );
  }, [job?.jobSkills]);

  // Matched skills are resolved server-side (exact catalog match + embedding
  // similarity), keyed by skill id so we can colour the chips we already render.
  const matchedSkillIds = useMemo(
    () => new Set(job?.matchedRequiredSkillIds ?? []),
    [job?.matchedRequiredSkillIds],
  );

  const skillsMatchCount = requiredSkills.filter((skill) =>
    matchedSkillIds.has(skill.id),
  ).length;
  const totalSkillsCount = requiredSkills.length;

  const getSkillsMatchBadgeClassName = (
    matchCount: number,
    totalCount: number,
  ): string => {
    if (matchCount === totalCount) {
      return 'bg-success';
    }
    if (matchCount > 0) {
      return 'bg-warning text-dark';
    }
    return 'bg-secondary';
  };

  const getSkillBadgeClassName = (isMatched: boolean): string => {
    return `job-skill-badge ${
      isMatched ? 'job-skill-badge-matched' : 'job-skill-badge-unmatched'
    }`;
  };

  const goBack = () => {
    history.push(recruitmentRoutes.careerOpportunities.route.create({}));
  };

  const handleGetReferrerLink = useCallback(async () => {
    try {
      const result = await getReferrerLink({ variables: { jobId } });
      const link = result.data?.recruiting?.meReferrerLink;
      if (!link) {
        addError(t('jobDetails.referrerLinkError'));
        return;
      }
      await navigator.clipboard.writeText(link);
      addSuccess(t('jobDetails.referrerLinkCopied'));
    } catch {
      addError(t('jobDetails.referrerLinkError'));
    }
  }, [getReferrerLink, addSuccess, addError, t, jobId]);

  if (error) {
    return <ServerError />;
  }

  if (loading) {
    return <Loading />;
  }

  if (!job) {
    return (
      <Container>
        <PageHeader
          title={t('jobDetails.notFound')}
          goBackButtonAction={goBack}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={
          <div>
            <div className="text-muted small mb-1">{t('jobDetails.title')}</div>
            <div className="h3 mb-0 fw-semibold">{job.Name}</div>
          </div>
        }
        goBackButtonAction={goBack}
      />
      <Row>
        <Col lg={8}>
          <Card className="mb-3">
            <Card.Body>
              {job.flair__Job_Description__c && (
                <div
                  className="mb-4 job-description"
                  dangerouslySetInnerHTML={{
                    __html: job.flair__Job_Description__c,
                  }}
                />
              )}

              {job.flair__Responsibilities__c && (
                <>
                  <h3 className="mt-4 mb-3 fw-semibold">
                    {t('jobDetails.responsibilities')}
                  </h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: job.flair__Responsibilities__c,
                    }}
                  />
                </>
              )}

              {job.flair__Job_Requirements__c && (
                <>
                  <h3 className="mt-4 mb-3 fw-semibold">
                    {t('jobDetails.requirements')}
                  </h3>
                  <div
                    dangerouslySetInnerHTML={{
                      __html: job.flair__Job_Requirements__c,
                    }}
                  />
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
        <Col lg={4}>
          <div className="job-sidebar">
            <Button
              variant="primary"
              className="w-100 mb-3"
              onClick={handleGetReferrerLink}
              disabled={referrerLinkLoading}>
              {referrerLinkLoading
                ? t('jobDetails.getReferrerLinkLoading')
                : t('jobDetails.getReferrerLink')}
            </Button>
            <Card className="mb-3">
              <Card.Body>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="fw-medium text-muted">
                    {t('jobDetails.jobTitle')}
                  </span>
                  <span>{job.Name}</span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="fw-medium text-muted">
                    {`${objectLabels.department}:`}
                  </span>
                  <span>{job.department?.Name ?? '-'}</span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="fw-medium text-muted">
                    {`${objectLabels.location}:`}
                  </span>
                  <span>{job.location?.Name ?? '-'}</span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="fw-medium text-muted">
                    {t('jobDetails.type')}
                  </span>
                  <span>
                    {job.flair__Type_of_Employment__c || '-'}
                    {job.flair__Time_Commitment__c
                      ? ` / ${job.flair__Time_Commitment__c}`
                      : ''}
                  </span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="fw-medium text-muted">
                    {t('jobDetails.publishedDate')}
                  </span>
                  <span>
                    {job.flair__Published_At__c
                      ? new Date(
                          job.flair__Published_At__c,
                        ).toLocaleDateString()
                      : '-'}
                  </span>
                </div>
                <div className="d-flex justify-content-between py-2 border-bottom">
                  <span className="fw-medium text-muted">
                    {t('jobDetails.status')}
                  </span>
                  <span className="d-inline-flex align-items-center gap-1">
                    <span
                      className={`status-badge-dot ${
                        (job.flair__Status__c ?? 'Open') === 'Open'
                          ? 'status-badge-dot-open'
                          : 'status-badge-dot-closed'
                      }`}
                    />
                    {job.flair__Status__c ?? t('jobDetails.statusOpen')}
                  </span>
                </div>
              </Card.Body>
            </Card>

            <Card>
              <Card.Body>
                <h3 className="job-skills-title">
                  {t('jobDetails.requiredSkills')}
                </h3>
                {totalSkillsCount === 0 ? (
                  <EmptyStateCardBody title={t('jobDetails.noSkillsDefined')} />
                ) : (
                  <>
                    <div className="mb-3">
                      <span
                        className={`badge ${getSkillsMatchBadgeClassName(
                          skillsMatchCount,
                          totalSkillsCount,
                        )}`}>
                        {t('jobDetails.skillsMatch', {
                          count: skillsMatchCount,
                          total: totalSkillsCount,
                        })}
                      </span>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                      {requiredSkills.map((skill) => (
                        <span
                          key={skill.id}
                          className={getSkillBadgeClassName(
                            matchedSkillIds.has(skill.id),
                          )}>
                          {skill.name}
                        </span>
                      ))}
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default JobDetails;
